import { isNil, get, includes } from 'lodash';

/**
 * @typedef {object} Feature
 * @typedef {object} Point
 */

import {
  LAYER_TYPES,
  LAYER_SUBTYPES,
  WMS_VERSIONS,
  GEOJSON_API_BASED_SUBTYPES,
  GEOREF_TYPES,
  RASTER_3D_MAPS_SUBTYPES
} from './Layers';

/**
 * Gets the layer id.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The id for the layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getId = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, '_id', '');
};

/**
 * Get layer name.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getName = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'name', '');
};

/**
 * Get layer description.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The description of the layer or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getDescription = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'description', '');
};

/**
 * Gets the layer type.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The layer type or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getType = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerType', '');
};

/**
 * Gets the layer subtype.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The layer sub type or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getSubType = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '');
};

/**
 * Checks if the layer is a spatial layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is spatial, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isSpatial = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerType', '') === LAYER_TYPES.SPATIAL;
};
/**
 * Checks if the layer is a spatial layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is spatial, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const is3D = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerType', '') === LAYER_TYPES.TRIDIMENSIONAL;
};

/**
 * Checks if the layer is a thematic layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is thematic, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isThematic = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerType', '') === LAYER_TYPES.THEMATIC;
};

/**
 * Checks if the layer is a join layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is join, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isJoin = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerType', '') === LAYER_TYPES.JOIN;
};

/**
 * Checks if the layer is a google sheet layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a google sheet, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isAnnotationsLayer = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.ANNOTATIONS;
};

/**
 * Checks if the layer is a WMS layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is WMS, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isWMS = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.WMS;
};

/**
 * Checks if the layer is a WMTS layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is WMTS, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isWMTS = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.WMTS;
};

/**
 * Checks if the layer is a TMS layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is TMS, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isTMS = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.TMS;
};

/**
 * Checks if the layer is a GeoJSON layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is GeoJSON, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isGeoJSON = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.GEOJSON;
};
/**
 * Checks if the layer is a GeoJSON layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is GeoJSON, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const is3DTile = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.CESIUM3DTILESET;
};

/**
 * Checks if the layer is a TopoJSON layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is TopoJSON, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isTopoJSON = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.TOPOJSON;
};

/**
 * Checks if the layer is a GeoTiff layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is GeoTiff, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isGeoTiff = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.GEOTIFF;
};

/**
 * Checks if the layer is a tileServer layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a tile Server, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isTileServer = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.TILE_SERVER;
};

/**
 * Checks if the layer is a PostgreSQL connection layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a PostgreSQL connection, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isPostgreSQL = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.POSTGRESQL;
};

/**
 * Checks if the layer is a Fiware connection layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a Fiware connection, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isFiware = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.FIWARE;
};

/**
 * Checks if the layer is a external server connection layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is an external server connection, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isExternalServer = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.EXTERNAL_SERVER;
};

/**
 * Checks if the layer is an uploaded file layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is an uploaded file, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isUploadedFile = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.UPLOADED_FILE;
};

/**
 * Checks if the layer is a csv layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a csv, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isCSV = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.CSV;
};

/**
 * Checks if the layer is a google sheet layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a google sheet, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isGoogleSheets = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.GOOGLE_SHEETS;
};

/**
 * Checks if the layer is a GeoJSON VT layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a GeoJSON VT, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isGeoJSONVT = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.GEOJSONVT;
};

/**
 * Checks if the layer is MapBox layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a MapBox, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isMapBox = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.MAPBOX;
};

/**
 * Checks if the layer is Realtime GeoJSON layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a Realtime GeoJSON, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isRealTime = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.REALTIME;
};

/**
 * Checks if the layer is Realtime Annotations layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a Realtime Annotations, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isAnnotationsRealTime = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.ANNOTATIONSREALTIME;
};

/**
 * Checks if the layer is OGR layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a OGR, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isOGR = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.OGR;
};

/**
 * Checks if the layer is Excel layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a Excel, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isExcel = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.EXCEL;
};

/**
 * Checks if the layer is Indoor layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a Indoor, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isIndoor = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.INDOOR;
};

/**
 * Checks if the layer is MapBox GL layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a MapBox GL, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isMapBoxGL = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.MAPBOXGL;
};

/**
 * Checks if the layer is GeoImage layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is a GeoImage, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isGeoImage = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerSubType', '') === LAYER_SUBTYPES.GEOIMAGE;
};

/**
 * Checks if the layer is based on the GeoJSON API.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is based on the GeoJSON API, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isGeoJSONApiBased = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return includes(GEOJSON_API_BASED_SUBTYPES, get(layerObject, 'layerSubType', ''));
};
/**
 * Checks if the layer is based on the GeoJSON API.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is based on the GeoJSON API, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const is3DMapsSubtypes = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return includes(RASTER_3D_MAPS_SUBTYPES, get(layerObject, 'layerSubType', ''));
};

/**
 * Get layer  URL.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The layer  URL or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getURL = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'url', '');
};

/**
 * Get tilematrixset code to load in the WMTS.
 *
 * @param {object} layerObject - The tilematrixset object.
 * @returns {string} The names of the tilematrixset to select in a WMTS as codes to enter in Leaflet or empty string if not found.
 * @throws {Error} If tilematrixset is null or undefined.
 */
export const getWMTSTileMatrixSetNames = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'tileMatrixSet', '');
};

/**
 * Get layers codes to load in the WMS.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The names of the layers to select in a WMS as codes to enter in Leaflet or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getWMSLayerNames = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerNames', '');
};

/**
 * Get layers titles to show in the WMS.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The names of the layers to select in a WMS in a human readable format or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getWMSLayerTitles = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'layerTitles', '');
};

/**
 * Get layers codes to load in the WMS.
 *
 * @param {object} layerObject - The style object.
 * @returns {string} The names of the styles to select in a WMS as codes to enter in Leaflet or empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getWMSLayerStyle = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'styleName', '');
};

/**
 * Get layer to show in the WMTS.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the layers to select in a WMTS in a human readable format or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getWMTSLayer = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'wmtsLayer', '');
};

/**
 * Get format to show in the WMTS.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the format to select in a WMTS in a human readable format or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getWMTSFormats = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'imageFormat', '');
};

/**
 * Get style to show in the WMTS.
 *
 * @param {object} layerObject - The style object.
 * @returns {string} The name of the style to select in a WMTS in a human readable format or empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getWMTSStyle = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'wmtsStyle', '');
};

/**
 * Get layer image URL.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The layer image URL or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getImageURL = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'imageUrl', '');
};

/**
 * Get the geometry type of the geojson layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The geometry type of the geojson layer. Empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getGeoJSONGeometry = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'geojsonGeometry', '');
};

/**
 * Get the geojson properties that can be used for styling leaflet.
 *
 * @param {object} layerObject - The layer object.
 * @returns {Array} The array that stores each property in the geojson or empty array if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getGeoJSONProps = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'geojsonProps', []);
};

/**
 * Checks if the layer is deleted.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer is in trash, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isDeleted = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'deleted', '') !== 'no';
};

/**
 * Get layer location.
 *
 * @param {object} layerObject - The layer object.
 * @returns {Feature<Point>} The location of the layer or an empty object if it's not defined.
 * @throws {Error} If layer is null or undefined.
 */
export const getMapLocation = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'mapLocation', {});
};

/**
 * Get layer location.
 *
 * @param {object} layerObject - The layer object.
 * @returns {object} The annotations props of the layer or an empty object if it's not defined.
 * @throws {Error} If layer is null or undefined.
 */
export const getAnnotationLayerProperties = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'annotationLayerProperties', {});
};

/**
 * Get layer location.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} The annotations props of the layer or an empty object if it's not defined.
 * @throws {Error} If layer is null or undefined.
 */
export const getShowTraceVideo = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'ShowTraceVideo', true);
};

/**
 * Get layer location.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} The annotations props of the layer or an empty object if it's not defined.
 * @throws {Error} If layer is null or undefined.
 */
export const getShowAnnotationsPoints = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'ShowAnnotations', true);
};

/**
 * Get layer bbox.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number[]} The bbox of the layer or an empty array if it's not defined.
 * @throws {Error} If layer is null or undefined.
 */
export const getBBox = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'mapLocation.bbox', []);
};

/**
 * Get layer attribution.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The layer attributino or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getAttribution = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'attribution', '');
};

/**
 * Get layer subdomains.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The layer subdomains or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getSubdomains = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'subdomains', '');
};

/**
 * Gets the prop used for the join.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The geojson prop used for the join, or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getGeoJSONPropToJoin = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'geojsonPropToJoin', '');
};

/**
 * Gets the key used for the join.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The thematic key used for the join, or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getThematicKeyToJoin = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'thematicKeyToJoin', '');
};

/**
 * Gets the id of the geojson layer used for the join.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The id of the geojson layer used for the join, or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getGeoJSONId = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'geojsonId', '');
};

/**
 * Gets the id of the thematic layer used for the join.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The id of the thematic layer used for the join, or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getThematicId = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'thematicId', '');
};

/**
 * Gets the minimun value for the Sorensen-Dice to join.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The minimun value for the Sorensen-Dice to join, or 0.5 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getSorensenDiceMinimun = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'sorensenDiceMinimun', 0.5);
};

/**
 * Gets the list of keys found in the thematic layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {Array} The list of keys, or empty array if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getThematicKeyList = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'thematicKeyList', []);
};

/**
 * Gets the id of the style selected for the layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The id of the selected style, or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getStyleId = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'styleId', '');
};

/**
 * Gets the name of the layer to draw.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the layer to draw, or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getMapBoxLayerName = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'mapboxLayerName', '');
};

/**
 * Get the MapBox properties that can be used for styling leaflet.
 *
 * @param {object} layerObject - The layer object.
 * @returns {Array} The array that stores each property in the MapBox or empty array if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getMapBoxProps = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'mapboxProps', []);
};

/**
 * Get the time in milliseconds for refreshing the RealTime Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The time in milliseconds for refreshing the RealTime Layer or 1000 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getInterval = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'interval', 1000);
};

/**
 * Get all the parameters for the connection in a PostgreSQL Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {object} The parameters for the connection in a PostgreSQL Layer or empty object if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getConnectionParams = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'connectionParams', {});
};

/**
 * Get the name of the schema for the connection in a PostgreSQL Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the schema for the connection in a PostgreSQL Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getSchema = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'connectionParams.schema', '');
};

/**
 * Get the name of the table for the connection in a PostgreSQL Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the table for the connection in a PostgreSQL Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getTable = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'connectionParams.table', '');
};

/**
 * Get the host for the connection in a PostgreSQL Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The host for the connection in a PostgreSQL Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getHost = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'connectionParams.host', '');
};

/**
 * Get the user for the connection in a PostgreSQL Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The user for the connection in a PostgreSQL Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getUser = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'connectionParams.user', '');
};

/**
 * Get the name of the database for the connection in a PostgreSQL Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the database for the connection in a PostgreSQL Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getDatabase = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'connectionParams.database', '');
};

/**
 * Get the password for the connection in a PostgreSQL Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The password for the connection in a PostgreSQL Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getPassword = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'connectionParams.password', '');
};

/**
 * Get the port number for the connection in a PostgreSQL Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The port number for the connection in a PostgreSQL Layer or 3000 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getPort = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'connectionParams.port', 3000);
};

/**
 * Get all the parameters for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {object} The parameters for the connection in a Fiware Layer or empty object if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareParams = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams', {});
};

/**
 * Get the user name for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The user name for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareUser = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.user', '');
};

/**
 * Get the password for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The password for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwarePassword = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.password', '');
};

/**
 * Get the host for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The host for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareHost = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.host', '');
};

/**
 * Get the number of the port for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The number or the port for the connection in a Fiware Layer or 0 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwarePort = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.port', 0);
};

/**
 * Get the url for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The url for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareURL = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.fiwareURL', '');
};

/**
 * Get the key of a codify ID for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The key of a codify ID for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareApplicationID = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.fiwareApplicationID', '');
};

/**
 * Get key of a secret codify user for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The key of a secret codify user for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareApplicationSecret = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.fiwareApplicationSecret', '');
};

/**
 * Get the version for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The version for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareVersion = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.fiwareVersion', '');
};

/**
 * Get the service for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The service for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareService = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.service', '');
};

/**
 * Get the subservice for the connection in a Fiware Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The subservice for the connection in a Fiware Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareSubservice = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareParams.subservice', '');
};

/**
 * Get the name of the Fiware layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the Fiware layer to fetch or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFiwareLayerName = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'fiwareLayerName', '');
};

/**
 * Get the georeferencing type of the layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The georeferencing type of the layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getGeorefType = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'georefType', '');
};

/**
 * Checks if the geogereferencing type is location.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the georeferencing is location, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isLocation = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'georefType', '') === GEOREF_TYPES.LOCATION;
};

/**
 * Checks if the geogereferencing type is longitude and latitude.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the georeferencing is longitude and latitude, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isLongLat = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'georefType', '') === GEOREF_TYPES.LONGLAT;
};

/**
 * Checks if the geogereferencing type is H3.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the georeferencing is H3, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isH3 = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'georefType', '') === GEOREF_TYPES.H3;
};

/**
 * Checks if the geogereferencing type is address.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the georeferencing is address, false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isAddress = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'georefType', '') === GEOREF_TYPES.ADDRESS;
};

/**
 * Get the name of the layer to fetch in a OGR Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the layer to fetch in a OGR Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getOGRLayerName = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'ogrLayerName', '');
};

/**
 * Get the attributes to fetch in a OGR Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The attributes to fetch in a OGR Layer or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getOGRAttributes = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'ogrAttributes', '');
};

/**
 * Get the separator in a CSV Layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The separator in a CSV Layer or comma if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getSeparator = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'separator', ',');
};

/**
 * Get name of the sheet of the xlsx.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The name of the sheet of the xlsx or empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getExcelSheet = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'excelSheet', '');
};

/**
 * Get the number of the first row of the xlsx.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The number of the first row or 1 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFirstRow = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'excelLimits.firstRow', 1);
};

/**
 * Get the number of the last row of the xlsx.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The number of the last row or 1 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getLastRow = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'excelLimits.lastRow', 1);
};

/**
 * Get the number of the first row of the xlsx.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The number of the first row or 1 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getFirstColumn = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'excelLimits.firstColumn', 1);
};

/**
 * Get the number of the last row of the xlsx.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if it gets the url and false if not.
 * @throws {Error} If layer is null or undefined.
 */
export const getIsFeature = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'isFeature', false);
};

/**
 * Get the names of the selected columns of the xlsx.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string[]} The names of the selected columns or empty array if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getExcelColumns = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'excelLimits.columns', []);
};

/**
 * Get the number of the last row of the xlsx.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The number of the last row or 1 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getLastColumn = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'excelLimits.lastColumn', 1);
};

/**
 * Check if layer data is shared.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer data is shared and false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isShared = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'sharedData', false);
};
/**
 * Check if layer data is shared.
 *
 * @param {object} layerObject - The layer object.
 * @returns {boolean} True if the layer data is shared and false otherwise.
 * @throws {Error} If layer is null or undefined.
 */
export const isSharedPrivate = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'sharedPrivateData', false);
};
/**
 * Get the public id for the layer data.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The public id for the layer data or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getPublicDataId = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'publicDataId', '');
};

/**
 * Get the access token for the MapBox GL layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The access token or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getAccessToken = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'accessToken', '');
};

/**
 * Get the object that stores the longitude and latitude of a Spatial Table.
 *
 * @param {object} layerObject - The layer object.
 * @returns {object} The object that contains the longitude and latitude of a Spatial Table or an empty object if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getTableSpatialObject = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'spatialTableFields', {});
};

/**
 * Get the key that contains the longitude of a Spatial Table.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The key that contains the longitude of a Spatial Table or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getTableLongitudeKey = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, ['spatialTableFields', 'longitude'], '');
};

/**
 * Get the key that contains the latitude of a Spatial Table.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The key that contains the latitude of a Spatial Table or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getTableLatitudeKey = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, ['spatialTableFields', 'latitude'], '');
};

/**
 * Get the key that contains H3 index.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The key that contains the H3 index or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getH3Index = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'h3Index', '');
};

/**
 * Get the version to show the WMS layer.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The version to show the WMS layer or a default version if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getWMSVersion = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'wmsVersion', WMS_VERSIONS.V130);
};

/**
 * Get image top right corner coordinates.
 *
 * @param {object} layerObject - The layer object.
 * @returns {object} The image top right corner coordinates or empty object if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getTopRight = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'geoImageCorners.topRight', {});
};

/**
 * Get image top left corner coordinates.
 *
 * @param {object} layerObject - The layer object.
 * @returns {object} The image top left corner coordinates or empty object if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getTopLeft = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'geoImageCorners.topLeft', {});
};

/**
 * Get image bottom left corner coordinates.
 *
 * @param {object} layerObject - The layer object.
 * @returns {object} The image bottom left corner coordinates or empty object if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getBottomLeft = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'geoImageCorners.bottomLeft', {});
};

/**
 * Gets the value of a geoTiff image resolution .
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The value of the resolution or 128 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getGeoTiffResolution = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'geoTiffResolution', 128);
};

/**
 * Get the key that contains location index.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The key that contains the location index or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getLocationIndex = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'locationIndex', '');
};

/**
 * Get the key that contains address index.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The key that contains the address index or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getAddressIndex = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'addressIndex', '');
};

/**
 * Get the key that contains indoor level.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} The key that contains the indoor level or an empty string if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getIndoorLevel = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'indoorLevel', '');
};

/**
 * Change the maximum zoom allowed.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The number of the max zoom allowed or 20 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getMaxZoom = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'maxZoom', 20);
};

/**
 * Change the minimum zoom allowed.
 *
 * @param {object} layerObject - The layer object.
 * @returns {number} The number of min zoom allowed or 0 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const getMinZoom = layerObject => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return get(layerObject, 'minZoom', 0);
};

/**
 * Checks if zoom level is valid.
 *
 * @param {object} layerObject - The layer object.
 * @param {object} zoom - The zoom level.
 * @returns {number} The number of min zoom allowed or 0 if not found.
 * @throws {Error} If layer is null or undefined.
 */
export const isZoomValid = (layerObject, zoom) => {
  if (isNil(layerObject)) throw new Error('layer not defined');
  return zoom >= getMinZoom(layerObject) && zoom <= getMaxZoom(layerObject);
};
